import { z } from 'zod'

/**
 * 사용자 프로필 — 지금은 닉네임 하나. 커뮤니티 글·댓글의 작성자 이름으로 쓰인다.
 * (닉네임이 없던 때는 Clerk 이름 → 이메일 앞부분이 그대로 노출됐다 — 2026-09-19)
 */
export const NICKNAME_MIN = 2
export const NICKNAME_MAX = 16

/** 글자·숫자(모든 언어) + . _ - 만. 공백·이모지·기호는 안 된다 */
const NICKNAME_RE = /^[\p{L}\p{N}._-]+$/u

/** 운영자로 오해할 수 있는 이름 (정규화한 값에 포함되면 거절) */
const RESERVED = [
  'admin',
  'administrator',
  'moderator',
  'official',
  'support',
  'staff',
  'recipecat',
  '운영자',
  '관리자',
  '레시피캣',
  '運営',
  '管理者',
  '官方',
  '管理员',
]

/** 중복 검사용 키: 호환 정규화 + 소문자. "Mina" 와 "ｍｉｎａ" 는 같은 닉네임 */
export function nicknameKey(nickname: string): string {
  return nickname.normalize('NFKC').trim().toLowerCase()
}

export type NicknameIssue = 'length' | 'chars' | 'reserved'

/** 앱이 입력 중에 바로 보여줄 수 있게 이유를 돌려준다 (문제 없으면 null) */
export function nicknameIssue(raw: string): NicknameIssue | null {
  const v = raw.normalize('NFKC').trim()
  const len = [...v].length
  if (len < NICKNAME_MIN || len > NICKNAME_MAX) return 'length'
  if (!NICKNAME_RE.test(v)) return 'chars'
  const key = nicknameKey(v)
  if (RESERVED.some((r) => key.includes(r))) return 'reserved'
  return null
}

export const nicknameSchema = z
  .string()
  .transform((v) => v.normalize('NFKC').trim())
  .refine((v) => nicknameIssue(v) === null, { message: 'Invalid nickname' })

export const updateProfileSchema = z.object({ nickname: nicknameSchema })
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>

export interface ProfileView {
  /** 아직 정하지 않았으면 null */
  nickname: string | null
}

/** PUT /me/profile 409 — 다른 사람이 쓰는 닉네임 */
export const NICKNAME_TAKEN_CODE = 'NICKNAME_TAKEN'

/**
 * DELETE /me · DELETE /admin/users/:id 503 의 error.code — 삭제가 끝나지 않음.
 * 다시 시도하면 남은 부분부터 이어서 지운다 (apps/api/src/lib/account.ts)
 */
export const ACCOUNT_DELETE_INCOMPLETE_CODE = 'ACCOUNT_DELETE_INCOMPLETE'

/**
 * 계정 삭제 이유 (2026-10-17, 1.0.4~ 앱). 떠나는 사람에게 한 문항만 묻는다 — 고르지 않아도 삭제된다.
 * 순서가 앱 화면 순서다. 서버는 삭제가 끝난 뒤 이유만 기록하고(누구인지는 남기지 않음) 운영자에게 알린다.
 */
export const ACCOUNT_DELETE_REASONS = [
  'not_useful',
  'too_expensive',
  'hard_to_use',
  'bugs',
  'privacy',
  'other',
] as const
export type AccountDeleteReason = (typeof ACCOUNT_DELETE_REASONS)[number]
/** 직접 쓰는 한 줄 의견 최대 글자 */
export const ACCOUNT_DELETE_DETAIL_MAX = 300

/** DELETE /me 본문 — 전부 선택. 옛 앱(본문 없음)도 그대로 통과한다 */
export const accountDeleteSchema = z.object({
  reason: z.enum(ACCOUNT_DELETE_REASONS).optional(),
  detail: z
    .string()
    .transform((v) => v.trim().slice(0, ACCOUNT_DELETE_DETAIL_MAX))
    .optional(),
})
export type AccountDeleteInput = z.infer<typeof accountDeleteSchema>
